import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../design/Icon';
import { imageUrl } from './helpData';

const CALLOUT = {
  note: { icon: 'info', label: 'Note' },
  tip: { icon: 'sparkle', label: 'Tip' },
  important: { icon: 'alert', label: 'Important' },
};

function Inline({ nodes, onLink }) {
  return nodes.map((node, i) => {
    if (node.t === 'text') return <React.Fragment key={i}>{node.v}</React.Fragment>;
    if (node.t === 'b') return <strong key={i}><Inline nodes={node.c} onLink={onLink} /></strong>;
    if (node.t === 'i') return <em key={i}><Inline nodes={node.c} onLink={onLink} /></em>;
    if (node.t === 'link') {
      if (node.kind === 'external') {
        return (
          <a key={i} href={node.href} target="_blank" rel="noreferrer noopener">
            <Inline nodes={node.c} onLink={onLink} />
          </a>
        );
      }
      const href = node.kind === 'app' ? node.href : `/help/${node.href}${node.anchor ? `#${node.anchor}` : ''}`;
      return (
        <a
          key={i}
          href={href}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onLink(node);
          }}
        >
          <Inline nodes={node.c} onLink={onLink} />
        </a>
      );
    }
    return null;
  });
}

function Lightbox({ image, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [onClose]);
  return createPortal(
    <div className="help-lightbox" role="dialog" aria-modal="true" aria-label={image.alt} onClick={onClose}>
      <button type="button" className="btn btn-ghost icon-btn help-lightbox-close" aria-label="Close image" ref={closeRef} onClick={onClose}>
        <Icon name="x" />
      </button>
      <img src={imageUrl(image.src)} alt={image.alt} />
    </div>,
    document.body
  );
}

function Figure({ block }) {
  const [zoomed, setZoomed] = useState(false);
  const close = useCallback(() => setZoomed(false), []);
  if (!block.src) return null;
  return (
    <figure className="help-figure">
      <button type="button" className="help-figure-button" onClick={() => setZoomed(true)} aria-label={`Enlarge: ${block.alt}`}>
        <img src={imageUrl(block.src)} alt={block.alt} width={block.width} height={block.height} loading="lazy" />
      </button>
      <figcaption>{block.alt}</figcaption>
      {zoomed && <Lightbox image={block} onClose={close} />}
    </figure>
  );
}

/** Renders a compiled help article (tools/user-guide/help-compiler.js block format). */
export function HelpArticleBody({ article, onLink }) {
  return (
    <div className="help-article-body">
      {article.blocks.map((block, i) => {
        switch (block.t) {
          case 'h2':
            return (
              <h2 key={i} id={block.id}>
                <Inline nodes={block.c} onLink={onLink} />
              </h2>
            );
          case 'h3':
            return (
              <h3 key={i} id={block.id}>
                <Inline nodes={block.c} onLink={onLink} />
              </h3>
            );
          case 'p':
            return (
              <p key={i}>
                <Inline nodes={block.c} onLink={onLink} />
              </p>
            );
          case 'ol':
          case 'ul': {
            const Tag = block.t;
            return (
              <Tag key={i} className={block.t === 'ol' ? 'help-steps' : undefined}>
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Inline nodes={item} onLink={onLink} />
                  </li>
                ))}
              </Tag>
            );
          }
          case 'callout': {
            const meta = CALLOUT[block.kind] || CALLOUT.note;
            return (
              <div key={i} className={`help-callout help-callout-${block.kind}`} role="note">
                <Icon name={meta.icon} />
                <p>
                  <strong>{meta.label}:</strong> <Inline nodes={block.c} onLink={onLink} />
                </p>
              </div>
            );
          }
          case 'table':
            return (
              <div key={i} className="help-table-wrap">
                <table className="help-table">
                  <thead>
                    <tr>
                      {block.head.map((cell, j) => (
                        <th key={j} scope="col">
                          <Inline nodes={cell} onLink={onLink} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, j) => (
                          <td key={j}>
                            <Inline nodes={cell} onLink={onLink} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'img':
            return <Figure key={i} block={block} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
